// The `/story` route for v3: reads the `clay-value-story-v1` deck snapshot through
// migrateDeckSnapshot (via story-route-model) and renders the matching state. index.tsx does not
// mount this yet — a later stage switches the route over.
//
//   story   -> the v3 deck
//   legacy  -> a v2 snapshot: no engine math here, so ask the SE to reopen the calculator
//   empty   -> nothing saved, or unreadable JSON

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { readStoredString } from '../../../../_shared/read-stored-json';
import { useAppBasePath } from '../../../../_shared/base-path';
import logoBlk from '../../../assets/logo/Clay_Logo_Primary_Blk.png';
import { ValueStoryDeckV3 } from './ValueStoryDeckV3';
import { STORY_KEY, resolveStoryRoute, type StoryRouteState } from './story-route-model';

function readRoute(): StoryRouteState {
  try {
    return resolveStoryRoute(readStoredString(localStorage, STORY_KEY));
  } catch {
    return { kind: 'empty' }; // storage unavailable
  }
}

function StateScreen({ title, body, onBack }: { title: string; body: string; onBack: () => void }) {
  return (
    <div
      className="vc-root"
      style={{
        minHeight: '100vh',
        background: 'var(--oat-100)',
        color: 'var(--oat-500)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        padding: '48px',
        textAlign: 'center',
      }}
    >
      <img src={logoBlk} alt="Clay" style={{ height: '40px', width: 'auto' }} />
      <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 575, fontSize: '36px', letterSpacing: '-0.03em', margin: 0 }}>
        {title}
      </h2>
      <p style={{ fontSize: '16px', color: 'var(--oat-400)', margin: 0, maxWidth: '520px', lineHeight: 1.5 }}>{body}</p>
      <button
        type="button"
        onClick={onBack}
        className="vc-hov-bg-oat200"
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: '13.5px',
          fontWeight: 500,
          padding: '10px 18px',
          borderRadius: '999px',
          background: 'var(--white)',
          border: '1px solid var(--grey-line)',
          color: 'var(--oat-500)',
          cursor: 'pointer',
        }}
      >
        Back to calculator
      </button>
    </div>
  );
}

export function StoryRouteV3() {
  const basePath = useAppBasePath();
  const navigate = useNavigate();
  const onBack = useCallback(() => navigate(basePath || '/'), [navigate, basePath]);
  const [route, setRoute] = useState<StoryRouteState>(readRoute);

  // The calculator can rewrite the snapshot while this route is open (another tab, or just before
  // navigating here). Re-read on storage / focus; the JSON compare keeps the state object stable
  // when nothing changed, so the slides do not remount on every focus.
  useEffect(() => {
    const reload = () => {
      const next = readRoute();
      setRoute((prev) => (JSON.stringify(next) !== JSON.stringify(prev) ? next : prev));
    };
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === STORY_KEY) reload();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', reload);
    };
  }, []);

  if (route.kind === 'story') return <ValueStoryDeckV3 story={route.story} onBack={onBack} />;
  if (route.kind === 'legacy') {
    return (
      <StateScreen
        title="Reopen this sizing in the calculator to refresh the deck"
        body="This value story was saved by an earlier version of the calculator. Open it in the calculator and the deck will rebuild from your inputs."
        onBack={onBack}
      />
    );
  }
  return (
    <StateScreen
      title="No value story yet."
      body="Size a business case in the calculator, then open the deck from the last step."
      onBack={onBack}
    />
  );
}
